import { AnchorHTMLAttributes, HTMLAttributes, LabelHTMLAttributes, createElement } from 'react';

import { TEXT_VARIANTS } from '@pukka/common/constants';
import { FontWeights, TextColors } from '@pukka/common/types';
import memoize from 'lodash/memoize';
import { twMerge } from 'tailwind-merge';

const COLORS = {
  primary: 'text-primary',
  secondary: 'text-secondary',
  black: 'text-primary',
  white: 'text-white',
  blue: 'text-brand',
  link: 'text-light-blue-500',
  neutral: 'text-neutral-500',
} satisfies {
  [k: string]: TextColors;
};

const WEIGHTS = {
  bold: 'font-bold',
  semibold: 'font-semibold',
  medium: 'font-medium',
  normal: 'font-normal',
} satisfies {
  [k: string]: FontWeights;
};

interface CommonTextProps {
  readonly variant?: keyof typeof TEXT_VARIANTS;
  readonly weight?: keyof typeof WEIGHTS;
  readonly color?: 'default' | keyof typeof COLORS;
}

type AnchorAttributes = keyof Omit<AnchorHTMLAttributes<HTMLElement>, keyof HTMLAttributes<HTMLElement>>;
type LabelAttributes = keyof Omit<LabelHTMLAttributes<HTMLElement>, keyof HTMLAttributes<HTMLElement>>;

type NoAnchorAttributes = {
  [k in AnchorAttributes]?: never;
};

type NoLabelAttributes = {
  [k in LabelAttributes]?: never;
};

interface TextParagraphProps extends HTMLAttributes<HTMLParagraphElement>, NoAnchorAttributes, NoLabelAttributes {
  readonly as: 'p';
}

interface TextLabelProps extends LabelHTMLAttributes<HTMLLabelElement>, NoAnchorAttributes {
  readonly as: 'label';
}

interface TextAnchorProps extends AnchorHTMLAttributes<HTMLAnchorElement>, NoLabelAttributes {
  readonly as: 'a';
}

interface TextHeadingProps extends HTMLAttributes<HTMLHeadingElement>, NoAnchorAttributes, NoLabelAttributes {
  readonly as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

interface TextInlineProps extends HTMLAttributes<HTMLSpanElement>, NoAnchorAttributes, NoLabelAttributes {
  readonly as: 'span' | 'b' | 'i' | 'q' | 's' | 'em' | 'samp' | 'strong' | 'sub' | 'sup' | 'small' | 'del' | 'dfn' | 'ins' | 'kbd';
}

type TextSpecificProps = TextParagraphProps | TextLabelProps | TextAnchorProps | TextHeadingProps | TextInlineProps;

type DefaultAs<T extends TextSpecificProps['as'] | undefined> = T extends undefined
  ? TextSpecificProps
  :
      | Exclude<TextSpecificProps, TextSpecificProps & { as: T }>
      | ({
          [key in keyof Omit<TextSpecificProps & { as: T }, 'as'>]: (TextSpecificProps & { as: T })[key];
        } & {
          as?: T | undefined;
        });

type TextProps<T extends TextSpecificProps['as'] | undefined> = CommonTextProps & DefaultAs<T>;

const memoizeClassName = memoize(
  (variant?: keyof typeof TEXT_VARIANTS, color?: keyof typeof COLORS | 'default', weight?: keyof typeof WEIGHTS, className?: string) => {
    return twMerge(
      variant && TEXT_VARIANTS[variant].size,
      color === 'default' ? variant && TEXT_VARIANTS[variant].color : color && COLORS[color],
      weight && WEIGHTS[weight],
      className,
    );
  },
  (v, c, w, cn) => {
    return `${v}|${c}|${w}|${cn}`;
  },
);

function Abstract<T extends TextProps<undefined>>({ as, variant, weight, color, className, children, ...props }: T): JSX.Element {
  return createElement(
    as,
    {
      ...props,
      className: memoizeClassName(variant, color, weight, className),
    },
    children,
  );
}

function Text({ as = 'p', variant, weight, color, ...props }: TextProps<'p'>): JSX.Element {
  return <Abstract as={as as 'p'} variant={variant} color={color} weight={weight} {...(props as TextProps<'p'>)} />;
}

Text.Abstract = Abstract;

export { Text, type TextProps };
